import {type InfiniteData, useQueryClient} from '@github-ui/react-query'

import {projectsQueryKey} from '../../queries/projects'
import type {NewProjectAction, ProjectsResponse} from './types'

interface CachedProjectsOwnerContext {
  viewerCanPush: boolean
  ownerLogin: string
  ownerIsOrganization: boolean
  newProjectAction: NewProjectAction | null
}

/**
 * Reads owner-context fields from any cached projects response for this owner/repo,
 * regardless of state/sort/template variant. Used to keep header chrome — the
 * "New project" CTA in particular — stable across the cross-route remount when a user
 * toggles Projects ↔ Templates: the new route's own query has no data yet, but a
 * sibling cache entry from the previous route already has what we need.
 *
 * Returns `undefined` when no cached response exists yet (e.g. first paint of a deep
 * link), so callers should still gracefully degrade — e.g. hide the CTA — until the
 * server confirms.
 */
export function useCachedProjectsOwnerContext(owner: string, repo: string): CachedProjectsOwnerContext | undefined {
  const queryClient = useQueryClient()
  const cached = queryClient.getQueriesData<InfiniteData<ProjectsResponse>>({
    queryKey: projectsQueryKey(owner, repo),
  })
  for (const [, data] of cached) {
    const firstPage = data?.pages[0]
    if (firstPage !== undefined) {
      return {
        viewerCanPush: firstPage.viewerCanPush,
        ownerLogin: firstPage.ownerLogin,
        ownerIsOrganization: firstPage.ownerIsOrganization,
        newProjectAction: firstPage.newProjectAction,
      }
    }
  }
  return undefined
}
